import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { propuestasVentaApi } from '../api/propuestasVenta'
import { listasApi } from '../api/listaPrecios'
import { descargarArchivo } from '../utils/descargar'
import { fmtMonto } from '../utils/fecha'
import { colors, text, sx } from '../design/tokens'
import Boton from '../components/Boton'
import MoneyInput from '../components/MoneyInput'
import SelectorArticulosModal from '../components/SelectorArticulosModal'
import useDirtyGuard from '../hooks/useDirtyGuard'
import { listaParaBuscar } from '../utils/listaVenta'

const fmtRef = n => '$ ' + fmtMonto(n)
let uidSeq = 1
// Grilla del PDF: 5 columnas × 5 filas por hoja. Casilla global = hoja·25 + fila·5 + columna.
const COLS = 5
const FILAS_HOJA = 5
const CASILLAS_HOJA = COLS * FILAS_HOJA
// La foto recortada (sin el blanco de los bordes), la misma que va al PDF.
const fotoRecortada = sku => `/api/publico/propuestas/foto/${encodeURIComponent(sku)}`
const primeraLibre = (ocupadas) => { let i = 0; while (ocupadas.has(i)) i++; return i }
const uid = () => 'u' + (uidSeq++) + Date.now().toString(36)

/**
 * Armado de una propuesta de venta: el vendedor elige artículos (foto + precio
 * sugerido de su lista), escribe sus precios/escalas y saca el PDF prolijo.
 */
export default function PropuestaVentaFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [form, setForm] = useState({
    // con_marca arranca APAGADO: por ahora la usa la empresa mayorista y no
    // se la quiere asociar con Ligier (Martin, 12 ago). El tilde queda para
    // las piezas que sí van con la marca.
    titulo: '', nota: '', lista_precios: '', vigencia_desde: '', vigencia_hasta: '', con_marca: false,
  })
  // Títulos que cortan una fila: la banda va ANTES de la fila global `fila`.
  const [secciones, setSecciones] = useState([])   // [{uid, titulo, fila}]
  const [items, setItems] = useState([])           // [{uid, posicion, nombre, nota, nuevo, destacado, sin_stock_override, escalas, articulos}]
  const [hojasVistas, setHojasVistas] = useState(1)
  const [seleccion, setSeleccion] = useState(null) // uid de la tarjeta que se edita
  const [arrastrando, setArrastrando] = useState(null)
  const [listas, setListas] = useState([])
  const [tokenPublico, setTokenPublico] = useState(null)
  const [showSelector, setShowSelector] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [error, setError] = useState(null)

  useDirtyGuard(dirty && !guardando)

  useEffect(() => {
    listasApi.listar().then(({ data }) => setListas(data.data || data)).catch(() => {})
    if (!id) return
    propuestasVentaApi.obtener(id).then(({ data }) => {
      setForm({
        titulo: data.titulo, nota: data.nota || '', lista_precios: data.lista_precios || '',
        vigencia_desde: data.vigencia_desde || '', vigencia_hasta: data.vigencia_hasta || '',
        con_marca: data.con_marca,
      })
      setSecciones((data.secciones || []).map(s => ({ uid: 's' + s.id, titulo: s.titulo, fila: s.fila })))
      setItems((data.items || []).map(it => ({
        uid: uid(),
        posicion: it.posicion,
        nombre: it.nombre, nota: it.nota || '',
        nuevo: it.nuevo, destacado: it.destacado,
        sin_stock_override: it.sin_stock_override,
        escalas: (it.escalas || []).map(e => ({ etiqueta: e.etiqueta || '', precio: String(e.precio ?? '') })),
        articulos: it.articulos || [],
      })))
      setTokenPublico(data.token_publico)
    }).catch(() => setError('No se pudo cargar la propuesta.'))
  }, [id])

  const marcar = (setter) => (...args) => { setter(...args); setDirty(true) }
  const setF = marcar((campo, valor) => setForm(f => ({ ...f, [campo]: valor })))
  const setItem = marcar((u, campo, valor) => setItems(prev => prev.map(it => it.uid === u ? { ...it, [campo]: valor } : it)))

  // ── Artículos ──────────────────────────────────────────────────────────
  // El selector único devuelve [{articulo, cantidad}]; acá solo importa el artículo.
  const agregarArticulos = async (seleccion) => {
    const arts = seleccion.map(s => s.articulo)
    if (!arts.length) return
    try {
      const { data } = await propuestasVentaApi.articulosInfo(form.lista_precios || null, arts.map(a => a.id))
      const porId = Object.fromEntries(data.map(a => [a.id, a]))
      setItems(prev => {
        // Cada botella nueva va a la primera casilla libre de la grilla.
        const ocupadas = new Set(prev.map(it => it.posicion))
        return [...prev, ...arts.map(a => {
        const info = porId[a.id] || a
        const posicion = primeraLibre(ocupadas)
        ocupadas.add(posicion)
        return {
          uid: uid(), posicion,
          // El nombre de la tarjeta es DETA_LISTA (Martin, 8 sep 2026): el
          // nombre de lista del maestro, sin el "V." interno de la descripción.
          nombre: info.deta_lista || info.descripcion || a.deta_lista || a.descripcion || '',
          nota: '', nuevo: false, destacado: false, sin_stock_override: null,
          escalas: [{ etiqueta: '', precio: info.precio_lista != null ? String(info.precio_lista) : '' }],
          articulos: [info],
        }
      })]
      })
      setDirty(true)
    } catch {
      setError('No se pudo traer la info de los artículos.')
    }
  }

  // La línea multi-producto (varias fotos, un nombre, un precio) se SACÓ
  // (Martin, 8 sep 2026: "no me gusta, poné los productos individuales"):
  // una tarjeta = un artículo.

  // ── Grilla: arrastrar una tarjeta a cualquier casilla (si está ocupada, se intercambian) ──
  const soltarEn = (posicion, u) => {
    setArrastrando(null)
    if (!u) return
    setItems(prev => {
      const movida = prev.find(it => it.uid === u)
      if (!movida || movida.posicion === posicion) return prev
      return prev.map(it => {
        if (it.uid === u) return { ...it, posicion }
        if (it.posicion === posicion) return { ...it, posicion: movida.posicion }
        return it
      })
    })
    setDirty(true)
  }

  // ── Escalas ────────────────────────────────────────────────────────────
  const setEscala = (u, i, campo, valor) => {
    setItems(prev => prev.map(it => it.uid !== u ? it : {
      ...it, escalas: it.escalas.map((e, j) => j === i ? { ...e, [campo]: valor } : e),
    }))
    setDirty(true)
  }
  const agregarEscala = (u) => setItem(u, 'escalas', [...items.find(it => it.uid === u).escalas, { etiqueta: '', precio: '' }])
  const quitarEscala = (u, i) => {
    const it = items.find(x => x.uid === u)
    if (it.escalas.length <= 1) return
    setItem(u, 'escalas', it.escalas.filter((_, j) => j !== i))
  }

  // ── Títulos de fila ────────────────────────────────────────────────
  const agregarTitulo = (fila) => {
    setSecciones(prev => [...prev, { uid: uid(), titulo: '', fila }])
    setDirty(true)
  }
  const setTitulo = (u, titulo) => { setSecciones(prev => prev.map(x => x.uid === u ? { ...x, titulo } : x)); setDirty(true) }
  const quitarTitulo = (u) => { setSecciones(prev => prev.filter(x => x.uid !== u)); setDirty(true) }

  const quitarItem = (u) => {
    setItems(prev => prev.filter(x => x.uid !== u))
    if (seleccion === u) setSeleccion(null)
    setDirty(true)
  }

  // ── Guardar / salidas ──────────────────────────────────────────────────
  const armarPayload = () => ({
    ...form,
    nota: form.nota || null,
    lista_precios: form.lista_precios || null,
    vigencia_desde: form.vigencia_desde || null,
    vigencia_hasta: form.vigencia_hasta || null,
    secciones: secciones.map(s => ({ uid: s.uid, titulo: s.titulo, fila: s.fila })),
    items: items.map(it => ({
      posicion: it.posicion,
      nombre: it.nombre, nota: it.nota || null,
      nuevo: it.nuevo, destacado: it.destacado,
      sin_stock_override: it.sin_stock_override,
      escalas: it.escalas
        .filter(e => e.precio !== '' && e.precio != null)
        .map(e => ({ etiqueta: e.etiqueta || null, precio: parseFloat(e.precio) || 0 })),
      articulo_ids: it.articulos.map(a => a.id),
    })),
  })

  const guardar = async () => {
    if (!form.titulo.trim()) { setError('Falta el título de la propuesta.'); return null }
    const sinPrecio = items.find(it => !it.escalas.some(e => e.precio !== '' && e.precio != null))
    if (sinPrecio) { setError(`"${sinPrecio.nombre}" no tiene ningún precio cargado.`); setSeleccion(sinPrecio.uid); return null }
    if (secciones.some(x => !x.titulo.trim())) { setError('Hay un título de fila vacío: escribilo o sacalo.'); return null }
    setGuardando(true)
    setError(null)
    try {
      const { data } = id
        ? await propuestasVentaApi.actualizar(id, armarPayload())
        : await propuestasVentaApi.crear(armarPayload())
      setDirty(false)
      setTokenPublico(data.token_publico)
      if (!id) navigate(`/propuestas-venta/${data.id}`, { replace: true })
      return data
    } catch (e) {
      setError(e.response?.data?.message || 'No se pudo guardar.')
      return null
    } finally {
      setGuardando(false)
    }
  }

  const guardarYPdf = async () => {
    const p = await guardar()
    if (p) descargarArchivo(`/propuestas-venta/${p.id}/pdf`, `propuesta_${p.id}.pdf`)
  }

  const linkPublico = tokenPublico ? `${window.location.origin}/api/publico/propuestas/${tokenPublico}/pdf` : null
  const copiarLink = async () => {
    const p = dirty || !id ? await guardar() : { token_publico: tokenPublico }
    if (!p) return
    await navigator.clipboard.writeText(`${window.location.origin}/api/publico/propuestas/${p.token_publico}/pdf`)
    alert('Link copiado. Pegalo donde quieras compartir la propuesta.')
  }
  const duplicarPropuesta = async () => {
    if (dirty) { const p = await guardar(); if (!p) return }
    const { data } = await propuestasVentaApi.duplicar(id)
    navigate(`/propuestas-venta/${data.id}`)
  }
  // (El botón de WhatsApp se sacó a pedido de Martin, 12 ago: la herramienta
  //  la usa por ahora la empresa mayorista y el mensaje/armado no debe
  //  asociarla con Ligier. La pieza se comparte bajando el PDF y adjuntándolo.)

  const porCasilla = useMemo(() => Object.fromEntries(items.map(it => [it.posicion, it])), [items])
  const titulosPorFila = useMemo(() => {
    const m = {}
    for (const t of secciones) (m[t.fila] ||= []).push(t)
    return m
  }, [secciones])
  // Se ven las hojas que tienen algo, más las que el vendedor agregó vacías.
  const hojasUsadas = Math.max(
    1,
    ...items.map(it => Math.floor(it.posicion / CASILLAS_HOJA) + 1),
    ...secciones.map(t => Math.floor(t.fila / FILAS_HOJA) + 1),
  )
  const hojas = Math.max(hojasUsadas, hojasVistas)
  const itemSel = items.find(it => it.uid === seleccion)

  return (
    <div style={{ maxWidth: 1200 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Boton variante="ghost" onClick={() => navigate(-1)}>← Volver</Boton>
          <h2 style={{ margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text }}>
            {id ? 'Propuesta de venta' : 'Nueva propuesta de venta'}
          </h2>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {id && <Boton variante="secondary" onClick={duplicarPropuesta} disabled={guardando}>⧉ Duplicar</Boton>}
          <Boton variante="secondary" onClick={copiarLink} disabled={guardando}>🔗 Link</Boton>
          <Boton variante="secondary" onClick={guardarYPdf} disabled={guardando}>⬇ PDF</Boton>
          <Boton variante="primary" onClick={guardar} disabled={guardando}>{guardando ? 'Guardando…' : '💾 Guardar'}</Boton>
        </div>
      </div>

      {error && <div style={{ background: colors.errBg, color: colors.err, padding: '10px 14px', borderRadius: 6, marginBottom: 12 }}>{error}</div>}

      {/* ── Datos de la pieza ── */}
      <div style={card}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <div>
            <label style={lbl}>Título</label>
            <input style={inp} value={form.titulo} onChange={e => setF('titulo', e.target.value)} placeholder="SUPER OFERTA CERVEZAS" />
          </div>
          <div>
            <label style={lbl}>Lista de referencia</label>
            <select style={inp} value={form.lista_precios} onChange={e => setF('lista_precios', e.target.value)}>
              <option value="">LISTA1 (default)</option>
              {listas.map(l => <option key={l.id} value={l.codigo}>{l.codigo}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 12, marginTop: 10, alignItems: 'end' }}>
          <div>
            <label style={lbl}>Nota (bajo el título)</label>
            <input style={inp} value={form.nota} onChange={e => setF('nota', e.target.value)} placeholder="VENTA POR PACK DE 24 UNIDADES" />
          </div>
          <div>
            <label style={lbl}>Vigencia desde</label>
            <input type="date" style={inp} value={form.vigencia_desde} onChange={e => setF('vigencia_desde', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>Vigencia hasta</label>
            <input type="date" style={inp} value={form.vigencia_hasta} onChange={e => setF('vigencia_hasta', e.target.value)} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: text.body, color: colors.text, paddingBottom: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.con_marca} onChange={e => setF('con_marca', e.target.checked)} />
            Con marca Ligier
          </label>
        </div>
      </div>

      {/* ── Barra de armado ── */}
      <div style={{ display: 'flex', gap: 8, margin: '14px 0', flexWrap: 'wrap', alignItems: 'center' }}>
        <Boton variante="secondary" onClick={() => setShowSelector(true)}>+ Agregar artículos</Boton>
        <Boton variante="secondary" onClick={() => setHojasVistas(hojas + 1)}>+ Hoja</Boton>
        <span style={{ fontSize: text.sec, color: colors.textSec }}>
          Así sale el PDF: arrastrá las botellas a cualquier casilla · «+ título» corta una fila · clic en una botella para editar su precio
        </span>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {Array.from({ length: hojas }, (_, h) => (
            <Hoja
              key={h} hoja={h} form={h === 0 ? form : null}
              porCasilla={porCasilla} titulosPorFila={titulosPorFila}
              seleccion={seleccion} arrastrando={arrastrando}
              onElegir={setSeleccion} onArrastrar={setArrastrando} onSoltar={soltarEn}
              onAgregarTitulo={agregarTitulo} onTitulo={setTitulo} onQuitarTitulo={quitarTitulo}
            />
          ))}
        </div>

        {/* Edición de la botella elegida */}
        <div style={{ flex: '1 1 300px', minWidth: 300, position: 'sticky', top: 12 }}>
          {itemSel ? (
            <ItemCard
              key={itemSel.uid} it={itemSel}
              onSet={(campo, valor) => setItem(itemSel.uid, campo, valor)}
              onEscala={(i, campo, valor) => setEscala(itemSel.uid, i, campo, valor)}
              onAgregarEscala={() => agregarEscala(itemSel.uid)}
              onQuitarEscala={i => quitarEscala(itemSel.uid, i)}
              onQuitar={() => quitarItem(itemSel.uid)}
              onCerrar={() => setSeleccion(null)}
            />
          ) : (
            <div style={{ ...card, color: colors.textSec, fontSize: text.sec }}>
              {items.length === 0
                ? 'Agregá artículos: entran con su foto y el precio de la lista como referencia, en la primera casilla libre.'
                : 'Hacé clic en una botella para editar su nombre, precio y escalas (pack, x50, pallet…).'}
            </div>
          )}
        </div>
      </div>

      {linkPublico && !dirty && (
        <div style={{ marginTop: 14, fontSize: text.sec, color: colors.textSec }}>
          Link público: <span style={{ ...sx.mono, userSelect: 'all' }}>{linkPublico}</span>
        </div>
      )}

      {showSelector && (
        <SelectorArticulosModal
          onConfirm={agregarArticulos}
          onClose={() => setShowSelector(false)}
          lista={listaParaBuscar(form.lista_precios)}
        />
      )}
    </div>
  )
}

/**
 * Una hoja A4 del PDF en pantalla: encabezado (solo la primera), y 5 filas de
 * 5 casillas. Cada casilla recibe una botella arrastrada; antes de cada fila
 * puede ir un título que la corta.
 */
function Hoja({ hoja, form, porCasilla, titulosPorFila, seleccion, arrastrando, onElegir, onArrastrar, onSoltar, onAgregarTitulo, onTitulo, onQuitarTitulo }) {
  const [sobre, setSobre] = useState(null)
  return (
    <div style={{ width: 760, background: colors.bg, border: `1px solid ${colors.border}`, boxShadow: '0 1px 4px rgba(15,23,42,0.08)', padding: '14px 12px 18px' }}>
      <div style={{ fontSize: text.meta, color: colors.textMeta, textAlign: 'right', marginBottom: 4 }}>Hoja {hoja + 1}</div>
      {form && (
        <div style={{ padding: '0 6px 8px' }}>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', color: PDF_TINTA }}>{form.titulo || 'Título de la propuesta'}</div>
          <div style={{ width: 50, height: 4, background: PDF_VERDE, margin: '6px 0 4px' }} />
          {form.nota && <div style={{ fontSize: 11, fontWeight: 700, color: PDF_VERDE, letterSpacing: 2, textTransform: 'uppercase' }}>{form.nota}</div>}
        </div>
      )}
      {Array.from({ length: FILAS_HOJA }, (_, f) => {
        const fila = hoja * FILAS_HOJA + f
        const titulos = titulosPorFila[fila] || []
        return (
          <div key={f}>
            {titulos.map(t => (
              <div key={t.uid} style={{ display: 'flex', alignItems: 'center', background: PDF_TINTA, borderLeft: `6px solid ${PDF_VERDE}`, margin: '6px 6px 2px', padding: '3px 6px' }}>
                <input
                  autoFocus={!t.titulo}
                  value={t.titulo}
                  onChange={e => onTitulo(t.uid, e.target.value)}
                  placeholder="TÍTULO DE LA FILA"
                  style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: 4, textTransform: 'uppercase' }}
                />
                <button onClick={() => onQuitarTitulo(t.uid)} title="Sacar el título" style={{ ...linkBtn, color: '#fff' }}>✕</button>
              </div>
            ))}
            {titulos.length === 0 && (
              <div style={{ height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <button onClick={() => onAgregarTitulo(fila)} style={{ ...linkBtn, fontSize: text.meta, color: colors.textMeta }}>+ título</button>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${COLS}, 1fr)`, gap: 6, padding: '0 6px' }}>
              {Array.from({ length: COLS }, (_, c) => {
                const pos = fila * COLS + c
                const it = porCasilla[pos]
                const esDestino = arrastrando && sobre === pos
                return (
                  <div
                    key={c}
                    onDragOver={e => { e.preventDefault(); if (sobre !== pos) setSobre(pos) }}
                    onDragLeave={() => setSobre(s => (s === pos ? null : s))}
                    onDrop={e => { e.preventDefault(); setSobre(null); onSoltar(pos, e.dataTransfer.getData('text/plain')) }}
                    style={{
                      height: 178,
                      border: it ? 'none' : `1px dashed ${esDestino ? colors.link : colors.border}`,
                      background: esDestino ? colors.linkBg : 'transparent',
                      outline: esDestino && it ? `2px solid ${colors.link}` : 'none',
                    }}
                  >
                    {it && (
                      <Tarjeta
                        it={it} elegida={seleccion === it.uid} fantasma={arrastrando === it.uid}
                        onClick={() => onElegir(it.uid)}
                        onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', it.uid); setTimeout(() => onArrastrar(it.uid), 0) }}
                        onDragEnd={() => { onArrastrar(null); setSobre(null) }}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/** La tarjeta como sale en el PDF: foto recortada, badges, nombre y precio principal. */
function Tarjeta({ it, elegida, fantasma, onClick, onDragStart, onDragEnd }) {
  const art = it.articulos[0]
  const sinStockAuto = it.articulos.length > 0 && it.articulos.every(a => a.sin_stock)
  const sinStock = it.sin_stock_override ?? sinStockAuto
  const principal = it.escalas[0]
  const extras = it.escalas.length - 1
  return (
    <div
      draggable onDragStart={onDragStart} onDragEnd={onDragEnd} onClick={onClick}
      title="Arrastrá para mover · clic para editar"
      style={{
        height: '100%', boxSizing: 'border-box', cursor: 'grab', userSelect: 'none',
        background: colors.bg,
        borderTop: `1px solid ${elegida ? colors.link : '#e5e7ea'}`,
        borderLeft: `1px solid ${elegida ? colors.link : '#e5e7ea'}`,
        borderRight: `1px solid ${elegida ? colors.link : '#e5e7ea'}`,
        borderBottom: `3px solid ${sinStock ? '#c7ccd2' : PDF_TINTA}`,
        boxShadow: elegida ? `0 0 0 2px ${colors.link}` : 'none',
        padding: '6px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center',
        opacity: fantasma ? 0.35 : 1,
      }}
    >
      <div style={{ height: 96, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
        {art?.foto_url
          ? <img src={fotoRecortada(art.codigo_alfa)} alt="" draggable={false} style={{ maxHeight: 96, maxWidth: '100%', opacity: sinStock ? 0.3 : 1 }} />
          : <span style={{ fontSize: 9, color: colors.textMeta, letterSpacing: 2 }}>SIN FOTO</span>}
      </div>
      <div style={{ minHeight: 13, marginTop: 3 }}>
        {it.nuevo && !sinStock && <span style={{ ...badgePdf, background: PDF_VERDE }}>Nuevo</span>}
        {sinStock && <span style={{ ...badgePdf, background: '#9aa0a8' }}>Sin stock</span>}
      </div>
      <div style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', textAlign: 'center', lineHeight: 1.2, color: PDF_TINTA, overflow: 'hidden', maxHeight: 33 }}>{it.nombre}</div>
      {principal?.precio !== '' && principal?.precio != null && !sinStock && (
        <div style={{ fontSize: 15, fontWeight: 800, color: PDF_VERDE, marginTop: 'auto' }}>{fmtRef(principal.precio)}</div>
      )}
      {extras > 0 && !sinStock && <div style={{ fontSize: 9, color: colors.textSec }}>+{extras} escala{extras > 1 ? 's' : ''}</div>}
    </div>
  )
}

function ItemCard({ it, onSet, onEscala, onAgregarEscala, onQuitarEscala, onQuitar, onCerrar }) {
  const sinStockAuto = it.articulos.length > 0 && it.articulos.every(a => a.sin_stock)
  const sinStock = it.sin_stock_override ?? sinStockAuto

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={lbl}>Botella elegida</span>
        <button onClick={onCerrar} style={linkBtn}>✕ cerrar</button>
      </div>
      <label style={lbl}>Nombre</label>
      <input style={{ ...inp, fontWeight: 600 }} value={it.nombre} onChange={e => onSet('nombre', e.target.value)} />
      <label style={{ ...lbl, marginTop: 8 }}>Nota</label>
      <input style={inp} value={it.nota} onChange={e => onSet('nota', e.target.value)} placeholder="nota (opcional)" />

      <div style={{ fontSize: text.sec, color: colors.textMeta, margin: '8px 0 4px' }}>
        {it.articulos.map(a => (
          <span key={a.id}>
            <span style={sx.mono}>{a.codigo_alfa}</span>
            {a.precio_lista != null && <> · lista {fmtRef(a.precio_lista)}</>}
            {a.sin_stock && <span style={{ color: colors.err }}> · sin stock</span>}
          </span>
        ))}
      </div>

      {/* Escalas de precio */}
      <label style={{ ...lbl, marginTop: 8 }}>Precios</label>
      {it.escalas.map((e, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
          <input
            style={{ ...inp, flex: 1, fontSize: text.sec }}
            value={e.etiqueta}
            onChange={ev => onEscala(i, 'etiqueta', ev.target.value)}
            placeholder={i === 0 ? 'principal (etiqueta opcional)' : 'PACK 24 / X PALLET…'}
          />
          <div style={{ width: 120 }}>
            <MoneyInput value={e.precio} onChange={v => onEscala(i, 'precio', v)} style={{ ...inp, textAlign: 'right', fontWeight: i === 0 ? 700 : 400 }} />
          </div>
          {it.escalas.length > 1 && <button onClick={() => onQuitarEscala(i)} style={linkBtn}>✕</button>}
        </div>
      ))}
      <button onClick={onAgregarEscala} style={{ ...linkBtn, marginTop: 4 }}>+ escala</button>

      <div style={{ display: 'flex', gap: 14, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={chk}><input type="checkbox" checked={it.nuevo} onChange={e => onSet('nuevo', e.target.checked)} /> NUEVO</label>
        <label style={chk} title="Automático desde el stock; tildá para forzar">
          <input
            type="checkbox"
            checked={sinStock}
            onChange={e => onSet('sin_stock_override', e.target.checked === sinStockAuto ? null : e.target.checked)}
          /> SIN STOCK {it.sin_stock_override == null && <span style={{ color: colors.textMeta }}>(auto)</span>}
        </label>
        <div style={{ marginLeft: 'auto' }}>
          <Boton variante="destructive" sm onClick={onQuitar}>🗑 Sacar</Boton>
        </div>
      </div>
    </div>
  )
}

// Colores de la PIEZA impresa (propuesta-venta.blade.php), no de la UI de LGR:
// la vista de la hoja tiene que verse como el PDF.
const PDF_TINTA = '#14181d'
const PDF_VERDE = '#1e7d32'
const badgePdf = { display: 'inline-block', fontSize: 7, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: '#fff', padding: '1px 6px' }
const card = { background: colors.bg, border: `1px solid ${colors.borderSoft}`, borderRadius: 8, padding: 14 }
const lbl = { display: 'block', fontSize: text.meta, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.textSec, marginBottom: 4 }
const inp = { width: '100%', padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: text.body, color: colors.text, background: colors.bg }
const chk = { display: 'flex', alignItems: 'center', gap: 6, fontSize: text.sec, color: colors.text, cursor: 'pointer' }
const linkBtn = { background: 'none', border: 'none', color: colors.link, cursor: 'pointer', fontSize: text.sec, padding: '2px 4px' }
